import React, { useMemo, useCallback } from "react";

import { UserAuth } from "../../context/AuthContext";

import "./Results.css";
import HeaderElement from "../../components/HeaderElement/HeaderElement";
import VoteType from "../../components/Voting/VotingCard/VoteType/VoteType";
import ResultsCard from "../../components/Voting/ResultsCard/ResultsCard";
import BarChartIcon from "@mui/icons-material/BarChart";
import copyIcon from "../../assets/icons/copy.svg";
import { Tooltip } from "react-tooltip";
import requestHandler from "../../utils/requestHandler";
import { useNavigate } from 'react-router-dom'
import PeopleIcon from '@mui/icons-material/People';
import StandardPopup from "../../components/Global/StandardPopup/StandardPopup";
import popupicon from "../../assets/icons/popup.svg";
import strings from "../../locales/ro.json";


const Results = ({ ballot }) => {
  const { user } = UserAuth();
  const navigate = useNavigate();
  const [showVotersModal, setShowVotersModal] = React.useState(false);
  const [votersData, setVotersData] = React.useState({ voters: [], seniors: [], juniors: [], alumni: [] });

  const [optionVotes, setOptionVotes] = React.useState([]);
  const [nrQualifiedVotes, setNrQualifiedVotes] = React.useState(0);
  const [expressedVotes, setExpressedVotes] = React.useState(0);
  const [totalVoters, setTotalVoters] = React.useState(0);
  const [isVoteInvalid, setIsVoteInvalid] = React.useState(false);
  const [users, setUsers] = React.useState([]);
  const [openInvalidationPopup, setOpenInvalidationPopup] = React.useState(false);
  const myAuthToken = useMemo(() => `Bearer ${user.accessToken}`, [user.accessToken]);

  // Memoize the users map for faster lookups
  const usersMap = useMemo(() => {
    return users.reduce((acc, user) => {
      acc[user.id] = user.name;
      return acc;
    }, {});
  }, [users]);

  // Memoize the copy text generation
  const copyText = useMemo(() => {
    if (!ballot || !optionVotes.length) return '';
    
    return `Title: ${ballot.title} - Created At: ${ballot.createdAt} - Closed At: ${ballot.closedAt} \n${
      ballot.question.question_text
    } \nVot ${ballot.showVotesPublicly ? "deschis" : "inchis"} \n${
      optionVotes.map(option => 
        `${option.name} : ${option.vote_count} ${
          ballot.showVotesPublicly && option.voters?.length ? 
          "(" + option.voters.join(", ") + ")" : ""
        }`
      ).join('\n')
    }`;
  }, [ballot, optionVotes]);

  const copyResultsToClipboard = useCallback(() => {
    navigator.clipboard.writeText(copyText);
  }, [copyText]);

  const fetchVotersData = async () => {
    try {
      const voters = await requestHandler(`ballot/${ballot.id}/voters`, "GET", `Bearer ${user.accessToken}`);
      const usersData = await requestHandler("users", "GET", `Bearer ${user.accessToken}`);
      
      const usersMap = usersData.reduce((acc, user) => {
        acc[user.id] = { name: user.name, statute: user.statute };
        return acc;
      }, {});

      const seniors = [];
      const juniors = [];
      const alumni = [];

      voters.voters.forEach(voterId => {
        const user = usersMap[voterId];
        if (user) {
          switch(user.statute) {
            case 'senior':
              seniors.push(user.name);
              break;
            case 'junior':
              juniors.push(user.name);
              break;
            case 'alumni':
              alumni.push(user.name);
              break;
          }
        }
      });

      setVotersData({ voters: voters.voters, seniors, juniors, alumni });
    } catch (error) {
      console.error('Error fetching voters data:', error);
    }
  };

  const archiveBallot = useCallback(async () => {
    try {
      const requests = [requestHandler(`ballot/${ballot.question.ballot_id}`, "DELETE", myAuthToken)];

      if (ballot?.juniorBallotId) {
        requests.push(
          requestHandler(`ballot/${ballot.juniorBallotId.id}`, "DELETE", myAuthToken)
        );
      }

      if (ballot?.alumniBallotId) {
        requests.push(
          requestHandler(`ballot/${ballot.alumniBallotId.id}`, "DELETE", myAuthToken)
        );
      }

      await Promise.all(requests);
      setTimeout(() => navigate("/"), 500);
    } catch (error) {
      console.error("Error archiving ballot:", error);
    }
  }, [ballot, myAuthToken, navigate]);

  React.useEffect(() => {
    if (!ballot) return;

    const fetchData = async () => {
      try {
        // Fetch users data in parallel with other operations if needed
        if (ballot.showVotesPublicly) {
          const usersData = await requestHandler("users", "GET", `Bearer ${user.accessToken}`);
          setUsers(usersData);
        }

        let totalVotersCount = ballot.question.voters.length;
        let options = ballot.question.options;

        // Handle split vote case
        if (ballot.juniorBallotId && ballot.alumniBallotId) {
          const [juniorVoters, alumniVoters] = await Promise.all([
            requestHandler(`ballot/${ballot.juniorBallotId.id}`, "GET", `Bearer ${user.accessToken}`),
            requestHandler(`ballot/${ballot.alumniBallotId.id}`, "GET", `Bearer ${user.accessToken}`)
          ]);

          totalVotersCount += juniorVoters.question.voters.length + alumniVoters.question.voters.length;
          if (juniorVoters.question.voters.length) totalVotersCount -= 1;
          if (alumniVoters.question.voters.length) totalVotersCount -= 1;

          const lastOption = options[options.length - 1];
          if (lastOption.vote_count > totalVotersCount / 2 + 1) {
            setIsVoteInvalid(true);
          }
        }

        // Process options and votes in a single pass
        if (ballot.showVotesPublicly) {
          const voteMap = ballot.votes.reduce((acc, vote) => {
            if (!acc[vote.option_id]) acc[vote.option_id] = [];
            acc[vote.option_id].push(usersMap[vote.user_id] || "Unknown User");
            return acc;
          }, {});

          options = options.map(option => ({
            ...option,
            voters: voteMap[option.id] || []
          }));
        }

        const expressedVotesTmp = ballot.isGeneralAssemblySplitVote
          ? options.reduce((sum, option, index) => 
              index === options.length - 1 ? sum : sum + option.vote_count, 0)
          : ballot.question.voters.length;

        setTotalVoters(totalVotersCount);
        setNrQualifiedVotes(ballot.question.voters.length);
        setExpressedVotes(expressedVotesTmp);
        setOptionVotes(options);
      } catch (error) {
        console.error('Error fetching ballot data:', error);
      }
    };

    fetchData();
  }, [ballot, user, usersMap]);

  const abstainOption = useMemo(() => 
    optionVotes[optionVotes.length - 1], 
    [optionVotes]
  );

  const getOtherVotes = useCallback((currentOption) => {
    if (!optionVotes.length) return [];
    return optionVotes
      .filter(option => option !== currentOption && option !== abstainOption)
      .map(option => option.vote_count);
  }, [optionVotes, abstainOption]);

  return (
    <div>
      <div className="resultsPageContainer">
        {ballot && (
          <div>
            <HeaderElement headerText={ballot.title} />
            <div className="resultsPageBodyContainer">
              <div className="metadataContainer">
                {ballot.isGeneralAssemblySplitVote && (
                  <>
                    <div className="ballotTitle">
                      {ballot.question.question_text}
                    </div>
                    <div className="resultsPageVoteCount">
                      Total voturi: {nrQualifiedVotes}
                    </div>
                  </>
                )}
                {!ballot.isGeneralAssemblySplitVote && (
                  <div className="ballotTitle">
                    {ballot.question.question_text}
                  </div>
                )}
                <div className="resultsPageVoteCount">
                  Total votanti: {totalVoters}
                </div>
                <VoteType voteType={ballot.showVotesPublicly} />
              </div>
              <div className="resultsPageOptionsContainer">
                {optionVotes.map((option, index) => (
                  <ResultsCard
                    key={option.id || index}
                    total={option !== abstainOption ? expressedVotes : nrQualifiedVotes}
                    name={option.name}
                    voteCount={option.vote_count}
                    countingRule={ballot.question.vote_couting_rule}
                    otherVotes={getOtherVotes(option)}
                    type={ballot.showVotesPublicly === 1 ? "public" : "private"}
                    isAbstain={index === optionVotes.length - 1 && ballot.isGeneralAssemblySplitVote}
                  >
                    {ballot.showVotesPublicly && option.voters.map((name, index2) => (
                      <div key={`${option.id}-${index2}`}>{name}</div>
                    ))}
                  </ResultsCard>
                ))}
              </div>
              <div className="resultsButtonsContainer">
                {ballot.parentBallotId === null && ballot.showVotesPublicly === 1 && ballot.isGeneralAssemblySplitVote &&( 
                  <button
                    className="voteStatusButton"
                    onClick={() => {
                      window.location.replace(`/ballot/${ballot.juniorBallotId.id}`)
                    }}
                  >
                    <BarChartIcon />
                    <div>Vezi Votul Juniorilor</div>
                  </button>
                )}
                {ballot.parentBallotId === null && ballot.showVotesPublicly === 1 && ballot.isGeneralAssemblySplitVote && (
                  <button
                    className="voteStatusButton"
                    onClick={() => {
                      window.location.replace(`/ballot/${ballot.alumniBallotId.id}`)
                    }}
                  >
                    <BarChartIcon />
                    <div>Vezi Votul Alumnilor</div>
                  </button>
                )}
                {ballot.parentBallotId !== null && ballot.showVotesPublicly === 1 && (
                  <button
                    className="voteStatusButton"
                    onClick={() => {
                      window.location.replace(`/ballot/${ballot.parentBallotId}`)
                    }}
                  >
                    <BarChartIcon />
                    <div>Vezi Rezultatul Final</div>
                  </button>
                )}
                {user.isAdmin && (
                  <button
                    className="voteStatusButton"
                    onClick={() => setOpenInvalidationPopup(true)}
                  >
                    <BarChartIcon />
                    <div>Arhivează Votul</div>
                  </button>
                )}
                <button
                  className="copyResultsButton"
                  data-tooltip-id="copyTooltip"
                  onClick={copyResultsToClipboard}
                >
                  <img src={copyIcon} alt="copy" />
                  <span>Copiază Rezultatul</span>
                </button>
              </div>
            </div>
            <StandardPopup 
              message={strings.editvote.invalidatePopup} 
              isOpen={openInvalidationPopup} 
              yesFcn={archiveBallot} 
              noFcn={() => setOpenInvalidationPopup(false)} 
              icon={popupicon}
            />
          </div>
        )}
        <Tooltip id="copyTooltip" content="Copiat!" events={["click"]} />
      </div>
      {showVotersModal && (
        <div className="voteStatusModalContainer">
          <div className="voteStatusModalVoters">
            <div className="voteStatusModalHeader">Au votat deja</div>
            <div className="voteStatusModalCount">
              {votersData.voters.length} voturi
            </div>
            
            {votersData.seniors.length > 0 && (
              <>
                <div className="voteStatusModalVotersStatute">
                  Seniori ({votersData.seniors.length}):
                </div>
                {votersData.seniors.map((senior, index) => (
                  <p key={`senior-${index}`}>{senior}</p>
                ))}
              </>
            )}

            {votersData.juniors.length > 0 && (
              <>
                <div className="voteStatusModalVotersStatute">
                  Juniori ({votersData.juniors.length}):
                </div>
                {votersData.juniors.map((junior, index) => (
                  <p key={`junior-${index}`}>{junior}</p>
                ))}
              </>
            )}

            {votersData.alumni.length > 0 && (
              <>
                <div className="voteStatusModalVotersStatute">
                  Alumni ({votersData.alumni.length}):
                </div>
                {votersData.alumni.map((alumni, index) => (
                  <p key={`alumni-${index}`}>{alumni}</p>
                ))}
              </>
            )}
          </div>
          <button
            className="voteStatusButton"
            onClick={() => setShowVotersModal(false)}
          >
            Închide
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(Results);
